import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
    Box,
    Typography,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { vortxColors } from '../core/theme.js';

// Constants
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Period colors - distinct, accessible palette
const PERIOD_COLORS = [
    '#1a365d', // Dark blue - Off-Peak/Base
    '#D4A84B', // Gold - Mid-Peak
    '#e74c3c', // Red/Coral - On-Peak
    '#9b59b6', // Purple - Super Off-Peak
    '#27ae60', // Green - Additional
    '#3498db', // Blue - Additional
];

/**
 * TOUHeatMapGrid
 * Visual 24x12 heat map grid for TOU schedule assignment.
 * Users can "paint" periods onto the grid by clicking/dragging.
 *
 * Props:
 * - periods: Array of period objects with { id, name, energy, demand }
 * - schedule: Object with { weekday: number[][], weekend: number[][] }
 *   Each is a 12x24 matrix where value = period ID
 * - onChange: Callback when schedule changes
 * - readOnly: If true, disable painting (for viewing loaded rates)
 */
function TOUHeatMapGrid({ periods = [], seasons = [], schedule, onChange, readOnly = false }) {
    // Active brush (selected period ID for painting)
    const [activeBrush, setActiveBrush] = useState(periods[0]?.id ?? 0);

    // Weekday/Weekend toggle
    const [scheduleType, setScheduleType] = useState('weekday');

    // Painting state
    const [isPainting, setIsPainting] = useState(false);
    const [dragStart, setDragStart] = useState(null); // { monthIdx, hourIdx }
    const [selectionPreview, setSelectionPreview] = useState(null); // { minMonth, maxMonth, minHour, maxHour }

    // Initialize schedule if not provided
    const [internalSchedule, setInternalSchedule] = useState(() => {
        if (schedule?.weekday?.length > 0) return schedule;
        // Default: all cells = period 0 (base/off-peak)
        const emptyGrid = () => Array(12).fill(null).map(() => Array(24).fill(0));
        return { weekday: emptyGrid(), weekend: emptyGrid() };
    });

    // Sync with prop changes - only if actually different
    useEffect(() => {
        if (schedule?.weekday?.length > 0 || schedule?.weekend?.length > 0) {
            // Only update if the schedule actually changed (avoid loop)
            const scheduleStr = JSON.stringify(schedule);
            const currentStr = JSON.stringify(internalSchedule);
            if (scheduleStr !== currentStr) {
                setInternalSchedule(schedule);
            }
        }
    }, [schedule]);

    // Get current grid based on weekday/weekend toggle
    const currentGrid = useMemo(() => {
        const grid = internalSchedule[scheduleType] || Array(12).fill(null).map(() => Array(24).fill(0));

        // If painting, overlay the selection preview
        if (isPainting && selectionPreview && !readOnly) {
            const previewGrid = grid.map(row => [...row]);
            for (let m = selectionPreview.minMonth; m <= selectionPreview.maxMonth; m++) {
                for (let h = selectionPreview.minHour; h <= selectionPreview.maxHour; h++) {
                    previewGrid[m][h] = activeBrush;
                }
            }
            return previewGrid;
        }
        return grid;
    }, [internalSchedule, scheduleType, isPainting, selectionPreview, activeBrush, readOnly]);

    // Update rectangular area
    const commitSelection = useCallback(() => {
        if (readOnly || !selectionPreview) return;

        setInternalSchedule(prev => {
            const newSchedule = { ...prev };
            const grid = prev[scheduleType].map(row => [...row]);

            for (let m = selectionPreview.minMonth; m <= selectionPreview.maxMonth; m++) {
                for (let h = selectionPreview.minHour; h <= selectionPreview.maxHour; h++) {
                    grid[m][h] = activeBrush;
                }
            }

            newSchedule[scheduleType] = grid;
            onChange?.(newSchedule);
            return newSchedule;
        });

        setIsPainting(false);
        setDragStart(null);
        setSelectionPreview(null);
    }, [selectionPreview, scheduleType, activeBrush, readOnly, onChange]);

    // Handle mouse events for painting
    const handleMouseDown = useCallback((monthIdx, hourIdx) => {
        if (readOnly) return;
        setIsPainting(true);
        setDragStart({ monthIdx, hourIdx });
        setSelectionPreview({ minMonth: monthIdx, maxMonth: monthIdx, minHour: hourIdx, maxHour: hourIdx });
    }, [readOnly]);

    const handleMouseEnter = useCallback((monthIdx, hourIdx) => {
        if (isPainting && !readOnly && dragStart) {
            // Calculate new bounds
            setSelectionPreview({
                minMonth: Math.min(dragStart.monthIdx, monthIdx),
                maxMonth: Math.max(dragStart.monthIdx, monthIdx),
                minHour: Math.min(dragStart.hourIdx, hourIdx),
                maxHour: Math.max(dragStart.hourIdx, hourIdx)
            });
        }
    }, [isPainting, readOnly, dragStart]);

    const handleMouseUp = useCallback(() => {
        if (isPainting) {
            commitSelection();
        }
    }, [isPainting, commitSelection]);

    // Get color for a period
    const getPeriodColor = useCallback((periodId) => {
        const idx = periods.findIndex(p => p.id === periodId);
        return PERIOD_COLORS[idx % PERIOD_COLORS.length] || PERIOD_COLORS[0];
    }, [periods]);

    // Get season for a month
    const getSeasonForMonth = useCallback((monthIdx) => {
        return seasons.find(s => s.months?.includes(monthIdx));
    }, [seasons]);

    // Format hour label
    const formatHour = (hour) => {
        if (hour === 0) return '12a';
        if (hour === 12) return '12p';
        if (hour < 12) return `${hour}a`;
        return `${hour - 12}p`;
    };

    return (
        <Box
            sx={{ userSelect: 'none' }}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
        >
            {/* Period Legend / Brush Selector */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>
                    {readOnly ? 'Legend:' : 'Paint with:'}
                </Typography>
                {periods.map((period, idx) => {
                    const color = PERIOD_COLORS[idx % PERIOD_COLORS.length];
                    const isActive = activeBrush === period.id;
                    // Strip season prefix for display
                    const displayName = period.name?.replace(/^(Winter|Summer)\s*/i, '') || period.name;
                    return (
                        <Chip
                            key={period.id}
                            label={`${displayName} ($${period.energy}/kWh)`}
                            size="small"
                            onClick={() => !readOnly && setActiveBrush(period.id)}
                            sx={{
                                bgcolor: color,
                                color: '#fff',
                                fontWeight: isActive ? 700 : 400,
                                border: isActive ? '2px solid #fff' : '2px solid transparent',
                                cursor: readOnly ? 'default' : 'pointer',
                                '&:hover': readOnly ? {} : { opacity: 0.9 }
                            }}
                        />
                    );
                })}
            </Box>

            {/* Weekday/Weekend Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <ToggleButtonGroup
                    value={scheduleType}
                    exclusive
                    onChange={(e, val) => val && setScheduleType(val)}
                    size="small"
                >
                    <ToggleButton value="weekday" sx={{ px: 2, py: 0.5 }}>
                        Weekdays
                    </ToggleButton>
                    <ToggleButton value="weekend" sx={{ px: 2, py: 0.5 }}>
                        Weekends
                    </ToggleButton>
                </ToggleButtonGroup>

                {!readOnly && (
                    <Typography variant="caption" color="text.secondary">
                        Click and drag to paint periods
                    </Typography>
                )}
            </Box>

            {/* Heat Map Grid */}
            <Box sx={{
                overflowX: 'auto',
                border: `1px solid ${alpha(vortxColors.gold, 0.3)}`,
                borderRadius: 1,
                p: 1,
                bgcolor: alpha('#000', 0.2)
            }}>
                {/* Hour Headers */}
                <Box sx={{ display: 'flex', ml: '48px', mb: 0.5 }}>
                    {HOURS.map(hour => (
                        <Box
                            key={hour}
                            sx={{
                                flex: 1,
                                minWidth: 20,
                                textAlign: 'center',
                                fontSize: '0.6rem',
                                color: 'text.secondary'
                            }}
                        >
                            {hour % 3 === 0 ? formatHour(hour) : ''}
                        </Box>
                    ))}
                </Box>

                {/* Grid Rows */}
                {MONTHS.map((month, monthIdx) => {
                    const season = getSeasonForMonth(monthIdx);
                    return (
                        <Box key={month} sx={{ display: 'flex', alignItems: 'center', mb: '2px' }}>
                            {/* Season Color Bar */}
                            <Box
                                sx={{
                                    width: 4,
                                    minWidth: 4,
                                    height: 20,
                                    bgcolor: season?.color || '#666',
                                    borderRadius: '2px',
                                    mr: 0.5
                                }}
                                title={season?.name || 'Unknown'}
                            />
                            {/* Month Label */}
                            <Typography
                                variant="caption"
                                sx={{
                                    width: 36,
                                    minWidth: 36,
                                    textAlign: 'right',
                                    pr: 1,
                                    fontSize: '0.7rem',
                                    color: season?.color || 'text.secondary'
                                }}
                            >
                                {month}
                            </Typography>

                            {/* Hour Cells */}
                            {HOURS.map(hourIdx => {
                                const periodId = currentGrid[monthIdx]?.[hourIdx] ?? 0;
                                const period = periods.find(p => p.id === periodId);
                                const color = getPeriodColor(periodId);

                                return (
                                    <Tooltip
                                        key={hourIdx}
                                        title={`${month} ${formatHour(hourIdx)}-${formatHour(hourIdx + 1)}: ${period?.name || 'Base'}`}
                                        arrow
                                        placement="top"
                                        open={isPainting ? false : undefined}
                                        disableHoverListener={isPainting}
                                        slotProps={{
                                            popper: {
                                                sx: { pointerEvents: 'none' }
                                            }
                                        }}
                                    >
                                        <Box
                                            onMouseDown={() => handleMouseDown(monthIdx, hourIdx)}
                                            onMouseEnter={() => handleMouseEnter(monthIdx, hourIdx)}
                                            sx={{
                                                flex: 1,
                                                minWidth: 20,
                                                height: 20,
                                                bgcolor: color,
                                                border: '1px solid rgba(0,0,0,0.3)',
                                                borderRadius: '2px',
                                                cursor: readOnly ? 'default' : 'pointer',
                                                transition: 'transform 0.1s, box-shadow 0.1s',
                                                '&:hover': readOnly ? {} : {
                                                    transform: 'scale(1.1)',
                                                    boxShadow: '0 0 8px rgba(255,255,255,0.3)',
                                                    zIndex: 1
                                                }
                                            }}
                                        />
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    );
                })}

                {/* Time Legend at Bottom */}
                <Box sx={{ display: 'flex', ml: '48px', mt: 0.5 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', width: '33%' }}>
                        Midnight
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', width: '33%', textAlign: 'center' }}>
                        Noon
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', width: '33%', textAlign: 'right' }}>
                        Midnight
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

export default TOUHeatMapGrid;
